/**
 * Debug-mode resource tracker: per-widget React commit cost + DOM size and
 * global heap / long-task / WS load, sampled every 5 s into a bounded ring.
 */
import { runCpuProbe } from '@/lib/debug/cpu-probe'
import { profileWidgetFibers, type WidgetFiberProfile } from '@/lib/debug/fiber-profile'
import type { StateSizes } from '@/lib/debug/state-probe'
import { getStreamStats } from '@/lib/debug/stream-stats'
import { getMountedWidgetCount } from '@/service/runtime-health/mounted-widget-registry'

export type WidgetTuple = [renders: number, commitMs: number, domNodes: number]

export type GlobalSample = {
  heapBytes: number | null
  /** Long-task ms accumulated since the previous sample. */
  longTaskMs: number
  /** Deltas since the previous sample, from getStreamStats(). */
  wsFrames: number
  wsBytes: number
  listenerCount: number
  mountedWidgets: number
  /**
   * Fixed-work JS benchmark (lib/debug/cpu-probe.ts), ~1 ms on a healthy
   * tab. Scales with how slow *all* JS runs here, not with app work — the
   * discriminator between "the app does more" and "this tab is throttled".
   */
  cpuProbeMs: number | null
  /** Whole-document element count; growth outside any widget shows here. */
  documentNodes: number | null
}

export type ResourceSample = {
  atMs: number
  global: GlobalSample
  /** Cumulative counters per widget id at sample time. */
  widgets: Record<string, WidgetTuple>
  /** Sizes of the shared collections (lib/debug/state-probe.ts); null when unavailable. */
  state: StateSizes | null
}

export type TrackerEvent = {
  atMs: number
  kind: 'recovery-suppressed' | 'reset'
  detail?: {
    reasons: string[]
    recommendedLevel: number
    score: number
    // Lifecycle context of the held cycle, so an export taken after a long
    // absence records how long the tab was hidden / frozen and whether the
    // sockets had to reconnect — the hold suppresses the reload that would
    // otherwise be the only trace of a ≥1h absence.
    hiddenDurationMs?: number
    executionGapMs?: number
    wasSuspended?: boolean
    reconnectCount?: number
  }
}

export type TrackedWidget = { widgetId: string; componentKey: string; pageId: string | null }

/** The subset of Element the sampler reads — lets tests pass a stub. */
export type TrackedElement = {
  querySelector: (
    selector: string
  ) => { querySelectorAll: (selector: string) => { length: number } } | null
}

export type ResourceExport = {
  exportedAt: string
  sessionAgeMs: number
  env: string | undefined
  userAgent: string | null
  widgets: TrackedWidget[]
  samples: ResourceSample[]
  events: TrackerEvent[]
  streamStats: ReturnType<typeof getStreamStats>
} & Record<string, unknown>

export const SAMPLE_INTERVAL_MS = 5_000
export const SAMPLE_CAPACITY = 360
export const EVENT_CAPACITY = 100

type Registration = TrackedWidget & {
  element: TrackedElement | null
  renders: number
  commitMs: number
  /** Component breakdown of the widget's slowest commit so far. */
  slowestCommit: SlowCommitProfile | null
}

export type SlowCommitProfile = WidgetFiberProfile & { capturedAtMs: number }

/** Commits at or above this are worth a fiber walk (one 60 Hz frame). */
export const SLOW_COMMIT_MS = 16

/** Per-sample probes beyond the counters; injectable so tests run without a store or DOM. */
export type SampleProbes = {
  cpuProbe: () => number | null
  documentNodes: () => number | null
  stateSizes: () => StateSizes | null
  profileWidget: (widgetId: string, element: TrackedElement | null) => WidgetFiberProfile | null
}

const defaultProbes: SampleProbes = {
  cpuProbe: () => runCpuProbe(),
  documentNodes: () =>
    typeof document === 'undefined' ? null : document.querySelectorAll('*').length,
  // No store access here: debug-session.ts wires the real reader so this
  // module stays importable from tests without the jotai store / storage atoms.
  stateSizes: () => null,
  profileWidget: (widgetId, element) =>
    typeof Element !== 'undefined' && element instanceof Element
      ? profileWidgetFibers(widgetId, element.querySelector('.widget-shell-content'))
      : null,
}

type IntervalScheduler = {
  setInterval: (callback: () => void, delayMs: number) => ReturnType<typeof setInterval>
  clearInterval: (id: ReturnType<typeof setInterval>) => void
}

type LongTaskObserverFactory = (
  onEntries: (durationsMs: number[]) => void
) => { disconnect: () => void } | undefined

const defaultScheduler: IntervalScheduler = {
  setInterval: (callback, delayMs) => setInterval(callback, delayMs),
  clearInterval: (id) => clearInterval(id),
}

const defaultCreateLongTaskObserver: LongTaskObserverFactory = (onEntries) => {
  try {
    const observer = new PerformanceObserver((list) => {
      onEntries(list.getEntries().map((entry) => entry.duration))
    })
    observer.observe({ entryTypes: ['longtask'] })
    return observer
  } catch {
    // Not supported here; heap and counters still sample.
    return undefined
  }
}

const state = {
  active: false,
  startedAtMs: 0,
  registrations: new Map<string, Registration>(),
  samples: [] as ResourceSample[],
  events: [] as TrackerEvent[],
  longTaskMsAccum: 0,
  lastWsFrames: 0,
  lastWsBytes: 0,
  stop: null as null | (() => void),
  probes: defaultProbes,
  now: Date.now as () => number,
}

export function registerTrackedWidget(
  widgetId: string,
  info: { componentKey: string; pageId: string | null; element: TrackedElement | null }
): void {
  state.registrations.set(widgetId, {
    widgetId,
    ...info,
    renders: 0,
    commitMs: 0,
    slowestCommit: null,
  })
}

export function unregisterTrackedWidget(widgetId: string): void {
  state.registrations.delete(widgetId)
}

/**
 * Profiler onRender feed. The profiling build fires onRender on every commit
 * of the Profiler fiber even when the memoised subtree bailed out, reporting
 * ~0 ms — so commit time is always honest but a "render" is only counted when
 * the commit did work.
 *
 * A commit that is both slow and the widget's slowest so far gets its fiber
 * tree walked right here, while the stamps still belong to it — the walk is
 * ~1 ms over a few thousand fibers and runs at most once per new maximum.
 */
export function recordRender(widgetId: string, actualDurationMs: number): void {
  if (!state.active) return
  const reg = state.registrations.get(widgetId)
  if (!reg) return
  reg.commitMs += actualDurationMs
  if (actualDurationMs > 0) reg.renders += 1
  if (actualDurationMs < SLOW_COMMIT_MS || actualDurationMs <= (reg.slowestCommit?.commitMs ?? 0)) {
    return
  }
  const profile = safe(() => state.probes.profileWidget(widgetId, reg.element))
  if (profile) reg.slowestCommit = { ...profile, capturedAtMs: state.now() }
}

export function isTrackingActive(): boolean {
  return state.active
}

function readHeapBytes(): number | null {
  if (typeof performance === 'undefined') return null
  const memory = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory
  return typeof memory?.usedJSHeapSize === 'number' ? memory.usedJSHeapSize : null
}

function countDomNodes(element: TrackedElement | null): number {
  // Count from the content wrapper so the shell's own hit-zone chrome (a
  // constant handful of nodes) stays out of the number; an unrevealed
  // placeholder has no content wrapper and reads 0.
  return element?.querySelector('.widget-shell-content')?.querySelectorAll('*').length ?? 0
}

function safe<T>(read: () => T): T | null {
  try {
    return read()
  } catch {
    return null
  }
}

function takeSample(getListenerCount: () => number, now: () => number, probes: SampleProbes): void {
  const stream = getStreamStats()
  const widgets: Record<string, WidgetTuple> = {}
  for (const reg of state.registrations.values()) {
    widgets[reg.widgetId] = [reg.renders, reg.commitMs, countDomNodes(reg.element)]
  }
  state.samples.push({
    atMs: now(),
    global: {
      heapBytes: readHeapBytes(),
      longTaskMs: state.longTaskMsAccum,
      wsFrames: stream.framesTotal - state.lastWsFrames,
      wsBytes: stream.bytesTotal - state.lastWsBytes,
      listenerCount: getListenerCount(),
      mountedWidgets: getMountedWidgetCount(),
      cpuProbeMs: safe(probes.cpuProbe),
      documentNodes: safe(probes.documentNodes),
    },
    widgets,
    state: safe(probes.stateSizes),
  })
  state.longTaskMsAccum = 0
  state.lastWsFrames = stream.framesTotal
  state.lastWsBytes = stream.bytesTotal
  if (state.samples.length > SAMPLE_CAPACITY) {
    state.samples.splice(0, state.samples.length - SAMPLE_CAPACITY)
  }
}

/**
 * Starts sampling. Idempotent: a second call while active returns a no-op stop
 * so the first caller stays the owner (two DashboardLayout instances can call
 * this — the shared-layout preview renders one too).
 */
export function startResourceSampler(deps: {
  getListenerCount: () => number
  scheduler?: IntervalScheduler
  createLongTaskObserver?: LongTaskObserverFactory
  now?: () => number
  probes?: Partial<SampleProbes>
}): () => void {
  if (state.active) return () => {}
  const scheduler = deps.scheduler ?? defaultScheduler
  const createObserver = deps.createLongTaskObserver ?? defaultCreateLongTaskObserver
  const now = deps.now ?? Date.now
  const probes: SampleProbes = { ...defaultProbes, ...deps.probes }

  state.active = true
  state.probes = probes
  state.now = now
  state.startedAtMs = now()
  const stream = getStreamStats()
  state.lastWsFrames = stream.framesTotal
  state.lastWsBytes = stream.bytesTotal

  const observer = createObserver((durationsMs) => {
    for (const durationMs of durationsMs) state.longTaskMsAccum += durationMs
  })
  const timer = scheduler.setInterval(
    () => takeSample(deps.getListenerCount, now, probes),
    SAMPLE_INTERVAL_MS
  )

  const stop = () => {
    if (state.stop !== stop) return
    scheduler.clearInterval(timer)
    observer?.disconnect()
    state.active = false
    state.stop = null
  }
  state.stop = stop
  return stop
}

export function getResourceSamples(): ResourceSample[] {
  // A copy, so a polling setState sees a new reference every tick.
  return state.samples.slice()
}

export function getTrackerEvents(): TrackerEvent[] {
  return state.events.slice()
}

export function recordTrackerEvent(event: Omit<TrackerEvent, 'atMs'>): void {
  state.events.push({ atMs: Date.now(), ...event })
  if (state.events.length > EVENT_CAPACITY) {
    state.events.splice(0, state.events.length - EVENT_CAPACITY)
  }
}

export function getTrackedWidgets(): TrackedWidget[] {
  return Array.from(state.registrations.values(), ({ widgetId, componentKey, pageId }) => ({
    widgetId,
    componentKey,
    pageId,
  }))
}

/** Each tracked widget's slowest commit since start/reset, slowest first. */
export function getSlowCommitProfiles(): SlowCommitProfile[] {
  return Array.from(state.registrations.values(), (reg) => reg.slowestCommit)
    .filter((p): p is SlowCommitProfile => p !== null)
    .sort((a, b) => b.commitMs - a.commitMs)
}

export function resetResourceTracking(): void {
  state.samples = []
  state.events = []
  state.longTaskMsAccum = 0
  for (const reg of state.registrations.values()) {
    reg.renders = 0
    reg.commitMs = 0
    reg.slowestCommit = null
  }
  recordTrackerEvent({ kind: 'reset' })
}

export function buildExport(extra: Record<string, unknown> = {}): ResourceExport {
  return {
    exportedAt: new Date().toISOString(),
    sessionAgeMs: state.active ? Date.now() - state.startedAtMs : 0,
    env: process.env.NEXT_PUBLIC_ENV,
    userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
    widgets: getTrackedWidgets(),
    samples: getResourceSamples(),
    events: getTrackerEvents(),
    streamStats: getStreamStats(),
    ...extra,
  }
}

/** Test-only. */
export function __resetResourceTrackerForTest(): void {
  state.stop?.()
  state.active = false
  state.startedAtMs = 0
  state.registrations.clear()
  state.samples = []
  state.events = []
  state.longTaskMsAccum = 0
  state.lastWsFrames = 0
  state.lastWsBytes = 0
  state.stop = null
  state.probes = defaultProbes
  state.now = Date.now
}
